<template>
    <div class="">
        <div class="container left">
            <div v-if="checkingAccess || loadingReportData">
                <div class="spacer s30"></div>
                <h3>
                    <span class="loading-spinner"><font-awesome-icon icon="spinner" class="fa-spin 3x" /></span>&nbsp; {{ status }}
                </h3>
            </div>

            <div class="spacer s10 desktop"></div>

            <div v-if="reportLoaded">
                <div class="spacer s20"></div>

                <!-- Audit info -->
                <div class="box">
                    <label class="label blue flex mb-4">
                        <span class="material-symbols-outlined">export_notes</span>&nbsp;Audit Information
                        <!-- <span v-if="currentReport.locked" class="red ml-3"> <font-awesome-icon icon="lock" />&nbsp;Audit Locked </span> -->
                    </label>

                    <!-- <pre>{{ currentReport }}</pre> -->

                    <table class="table clean left">
                        <tbody>
                            <tr>
                                <th>Audit Title</th>
                                <td>
                                    <p class="bold blue caps mb-0">{{ currentReport.title || 'Untitled' }}</p>
                                </td>
                            </tr>
                            <tr>
                                <th>Date</th>
                                <td>{{ simpleDate(currentReport.timestamp, true) }}</td>
                            </tr>
                            <tr>
                                <th>Reviewed By</th>
                                <td>{{ currentReport.username }}</td>
                            </tr>
                            <tr v-if="currentReport?.generalNotes">
                                <th>General Notes</th>
                                <td>{{ currentReport.generalNotes }}</td>
                            </tr>
                            <tr>
                                <th>Asset</th>
                                <td>
                                    <div class="is-flex is-align-items-center" style="gap: 12px">
                                        <img
                                            v-if="vessel.avatarUrl"
                                            :src="vessel.avatarUrl"
                                            class="thumb-img clickable"
                                            @click="openImageModal(vessel.avatarUrl)" />
                                        <span>{{ vessel.vesselName }}</span>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <th>Organisation</th>
                                <td>
                                    <div class="is-flex is-align-items-center" style="gap: 12px">
                                        <img
                                            v-if="fleet.newFleetLogo"
                                            :src="fleet.newFleetLogo"
                                            class="thumb-img clickable"
                                            @click="openImageModal(fleet.newFleetLogo)" />
                                        <span>{{ fleet.companyName }}</span>
                                    </div>
                                </td>
                            </tr>
                            <!-- <tr>
                                <th>
                                    Reviewed Items
                                    <div
                                        v-if="doneTally > 0"
                                        class="button is-small mt-3"
                                        :class="[hideDone ? 'is-success' : 'is-light']"
                                        @click="hideDone = !hideDone">
                                        {{ hideDone ? 'Show ' + doneTally + ' Resolved' : 'Hide ' + doneTally + ' Resolved' }}
                                    </div>
                                </th>
                                <td></td>
                            </tr> -->
                            <tr v-if="checklistLoaded && checklistSummary.total > 0">
                                <th>Audit Responses</th>
                                <td>
                                    <div class="tags in-table mt-2 mb-0">
                                        <span class="tag" :class="checklistSummary.yes > 0 ? 'is-success' : 'is-light'">
                                            <font-awesome-icon icon="check" />&nbsp;Yes: {{ checklistSummary.yes }}
                                        </span>
                                        <span class="tag" :class="checklistSummary.no > 0 ? 'is-danger' : 'is-light'">
                                            <font-awesome-icon icon="times" />&nbsp;No: {{ checklistSummary.no }}
                                        </span>
                                        <span class="tag" :class="checklistSummary.partial > 0 ? 'is-warning' : 'is-light'">
                                            &minus;&nbsp;Partial: {{ checklistSummary.partial }}
                                        </span>
                                        <span class="tag is-light">N/A: {{ checklistSummary.na }}</span>
                                        <span class="tag" :class="checklistSummary.comments > 0 ? 'is-info' : 'is-light'">
                                            <font-awesome-icon icon="comment" />&nbsp;Comments: {{ checklistSummary.comments }}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div v-if="statItems.length > 0" class="mt-4">
                        <h5>Snapshot Alerts</h5>
                        <div class="vessel-stats-bar mt-4">
                            <VesselStatCounter
                                v-for="(stat, idx) in statItems"
                                :key="'snap-stat-' + idx + '-' + statsAnimKey"
                                :label="stat.label"
                                :value="stat.value"
                                :color="stat.color"
                                :icon="stat.icon"
                                :animDelay="idx * 350"
                                :animated="animateStats" />
                        </div>
                        <p v-if="snapshotTimestamp" class="is-size-7 has-text-grey mt-2">
                            <font-awesome-icon icon="clock" />&nbsp; Snapshot saved: {{ simpleDate(snapshotTimestamp, true) }}
                        </p>
                    </div>
                </div>

                <!-- Tabs -->
                <div class="box mb-6">
                    <div class="tabs is-boxed">
                        <ul>
                            <li :class="{ 'is-active': tab == '1' }" @click="tab = '1'"><a>Summary</a></li>
                            <!-- <li :class="{ 'is-active': tab == '2' }" @click="tab = '2'"><a>Findings</a></li> -->
                            <li :class="{ 'is-active': tab == '3' }" @click="tab = '3'"><a>Audit Checklist</a></li>
                            <li v-if="reviewedItems.length > 0" :class="{ 'is-active': tab == '4' }" @click="tab = '4'"><a>Reviewed Items</a></li>
                            <li :class="{ 'is-active': tab == '5' }" @click="tab = '5'"><a>Follow Up</a></li>
                            <li :class="{ 'is-active': tab == '6' }" @click="tab = '6'"><a>Timeline</a></li>
                        </ul>
                    </div>

                    <div class="tab-content">
                        <!-- Tab 1: Notes -->
                        <div class="fullwidth" v-if="tab == '1'">
                            <label class="label blue flex mb-4">
                                <span class="material-symbols-outlined">export_notes</span>&nbsp;Summary Notes
                            </label>
                            <LiveDynamicChecklist
                                key="summary"
                                :readOnly="true"
                                inputType="paragraph"
                                :collectionPath="'vessels/' + weblinkMeta.vesselID + '/smsAssetReviews/' + weblinkMeta.auditID + '/summary'" />
                        </div>

                        <!-- Tab 2: Findings -->
                        <div class="fullwidth" v-if="tab == '2'">
                            <label class="label blue flex mb-4">
                                <span class="material-symbols-outlined">export_notes</span>&nbsp;Findings / Recommendations
                            </label>
                            <LiveDynamicChecklist
                                key="findings"
                                :readOnly="true"
                                inputType="para-para"
                                :collectionPath="
                                    'vessels/' + weblinkMeta.vesselID + '/smsAssetReviews/' + weblinkMeta.auditID + '/findings-recommendations'
                                " />
                        </div>

                        <!-- Tab 3: Audit Checklist -->
                        <div class="fullwidth" v-if="tab == '3'">
                            <label class="label blue flex mb-4">
                                <span class="material-symbols-outlined">checklist</span>&nbsp;Audit Checklist
                            </label>
                            <div v-if="loadingChecklist" class="has-text-grey py-3">
                                <font-awesome-icon icon="spinner" class="fa-spin" />&nbsp;Loading checklist...
                            </div>
                            <div v-else-if="checklistCategories.length === 0" class="has-text-grey py-5">
                                <p>No audit checklist found for this organisation.</p>
                            </div>
                            <div v-else class="columns">
                                <div class="column">
                                    <div v-for="category in checklistCategories" :key="category.id" class="box mb-4">
                                        <p class="has-text-weight-bold is-size-6 mb-3" :class="{ red: category.scoreExempt }">
                                            {{ category.title }}
                                        </p>
                                        <div v-if="questionsForCategory(category.id).length === 0" class="has-text-grey-light is-size-7">
                                            No questions in this category.
                                        </div>
                                        <div v-for="question in questionsForCategory(category.id)" :key="question.id" class="checklist-question-row">
                                            <div class="question-top">
                                                <span class="question-label">{{ question.question }}</span>
                                                <div class="select ml-3" style="flex-shrink: 0">
                                                    <select
                                                        disabled
                                                        :value="(checklistResponses[question.id] && checklistResponses[question.id].response) || ''"
                                                        :class="{
                                                            'has-text-success': (checklistResponses[question.id] || {}).response === 'Yes',
                                                            'has-text-danger': (checklistResponses[question.id] || {}).response === 'No',
                                                            'has-text-warning': (checklistResponses[question.id] || {}).response === 'Partial'
                                                        }">
                                                        <option value="">Not answered</option>
                                                        <option v-for="opt in ['Yes', 'No', 'Partial', 'N/A']" :key="opt" :value="opt">
                                                            {{ opt }}
                                                        </option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div
                                                v-if="checklistResponses[question.id] && checklistResponses[question.id].comment"
                                                class="is-size-7 has-text-grey mt-1 ml-1 firestore-para">
                                                {{ checklistResponses[question.id].comment }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="column is-one-third">
                                    <div v-if="hasChartData" class="chart-wrapper">
                                        <DoughnutChart :chartData="checklistChartData" :options="checklistChartOptions" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Tab 4: Reviewed Items -->
                        <div class="fullwidth" v-if="tab == '4'">
                            <label class="label blue flex mb-4"> <span class="material-symbols-outlined">checklist</span>&nbsp;Reviewed Items </label>
                            <div class="box clean">
                                <div v-if="reviewedItems.length > 0">
                                    <div v-for="(item, inx) in reviewedItems" :key="`items-${inx}`">
                                        <div v-if="!hideDone || !item.done" class="follow-up">
                                            <div class="label mt-2 mb-0" :class="{ orange: item.edited }">{{ item.title }}</div>
                                            <div class="heading mb-0">Content section: {{ contentTypes[item.reviewSlug] }}</div>
                                            <div class="summary mt-2 notification mb-1">
                                                <div v-if="!item.notes || item.notes.length == 0">
                                                    <em>No notes provided</em>
                                                </div>
                                                <div v-else>
                                                    <p v-for="(bit, ix) in item.notes.split('\n')" :key="`item-bit-${ix}`">{{ bit }}</p>
                                                </div>
                                            </div>
                                            <div v-if="item.edited" class="orange heading mb-2">
                                                <font-awesome-icon icon="pen" />&nbsp;Content Edited
                                            </div>
                                            <span class="button is-small is-static" :class="[item.done ? 'is-success' : 'is-warning']">
                                                {{ item.done ? 'Resolved' : 'Unresolved' }}
                                            </span>
                                            <hr />
                                        </div>
                                    </div>
                                </div>
                                <div v-else>No items reviewed yet.</div>
                            </div>
                        </div>

                        <!-- Tab 5: Follow Up -->
                        <div class="fullwidth" v-if="tab == '5'">
                            <label class="label blue flex mb-4">
                                <span class="material-symbols-outlined">export_notes</span>&nbsp;Follow Up Actions
                            </label>
                            <LiveDynamicChecklist
                                key="actions"
                                :readOnly="true"
                                :collectionPath="'vessels/' + weblinkMeta.vesselID + '/smsAssetReviews/' + weblinkMeta.auditID + '/actions'" />
                        </div>

                        <!-- Tab 6: Timeline -->
                        <div class="fullwidth" v-if="tab == '6'">
                            <label class="label blue flex mb-4"> <span class="material-symbols-outlined">history</span>&nbsp;Audit Timeline </label>
                            <div v-if="loadingTimeline" class="has-text-grey py-3">
                                <font-awesome-icon icon="spinner" class="fa-spin" />&nbsp;Loading...
                            </div>
                            <div v-else-if="timelineEntries.length === 0" class="has-text-grey py-3">No timeline entries yet.</div>
                            <div v-for="entry in timelineEntries" :key="entry.id" class="timeline-entry">
                                <div class="timeline-dot"></div>
                                <div class="timeline-body">
                                    <p class="timeline-text">{{ entry.text }}</p>
                                    <p class="timeline-meta">{{ simpleDate(entry.timestamp, true) }} &mdash; {{ entry.username }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <SimpleModal v-if="showImageModal" :fullWidth="false" cancelText="Close" showCancel @closed="showImageModal = false" theme="is-primary">
                <img :src="modalImageUrl" style="max-width: 100%; display: block; margin: 0 auto" />
            </SimpleModal>

            <div v-else-if="!checkingAccess && !loadingReportData">
                <div class="spacer s30"></div>
                {{ errorMessage }}
            </div>
        </div>
    </div>
</template>

<script>
import { db } from '../main.js';
import { collection, query, where, doc, orderBy, getDocs, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import LiveDynamicChecklist from '@/reusables/LiveDynamicChecklist.vue';
import DoughnutChart from '@/reusables/DoughnutChart.vue';
import VesselStatCounter from '@/reusables/VesselStatCounter.vue';
import { simpleDate } from '@/utilities/helperFunctions';

const aus_functions = getFunctions(undefined, 'australia-southeast1');
const loadAssetAuditReport = httpsCallable(aus_functions, 'asset_functions-loadAssetAuditReport');
const SimpleModal = () => import('@/reusables/SimpleModal.vue');

export default {
    components: {
        LiveDynamicChecklist,
        DoughnutChart,
        VesselStatCounter,
        SimpleModal
    },

    computed: {
        weblinkMeta() {
            return this.$store.getters.getCurrentWeblinkMeta;
        },
        scoredQuestions() {
            const exemptCatIDs = new Set(this.checklistCategories.filter((c) => c.scoreExempt).map((c) => c.id));
            return this.checklistQuestions.filter((q) => !exemptCatIDs.has(q.categoryID));
        },
        checklistSummary() {
            const validIDs = new Set(this.scoredQuestions.map((q) => q.id));
            const vals = Object.entries(this.checklistResponses)
                .filter(([id]) => validIDs.has(id))
                .map(([, r]) => r);
            return {
                yes: vals.filter((r) => r.response === 'Yes').length,
                no: vals.filter((r) => r.response === 'No').length,
                partial: vals.filter((r) => r.response === 'Partial').length,
                na: vals.filter((r) => r.response === 'N/A').length,
                comments: vals.filter((r) => r.comment && r.comment.trim() !== '').length,
                total: vals.length
            };
        },
        checklistChartData() {
            const s = this.checklistSummary;
            return {
                labels: ['Yes', 'No', 'Partial', 'N/A'],
                datasets: [
                    {
                        data: [s.yes, s.no, s.partial, s.na],
                        backgroundColor: ['#23d160', '#ff3860', '#ffdd57', '#dbdbdb'],
                        borderWidth: 1
                    }
                ]
            };
        },
        checklistChartOptions() {
            return {
                responsive: true,
                plugins: {
                    legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
                    tooltip: { enabled: true }
                }
            };
        },
        hasChartData() {
            const s = this.checklistSummary;
            return s.yes + s.no + s.partial + s.na > 0;
        },
        statItems() {
            return this.currentReport?.snapshotAlerts ?? [];
        },
        snapshotTimestamp() {
            return this.currentReport?.snapshotAlertsTimestamp ?? null;
        }
    },

    data() {
        return {
            xID: '',
            status: 'Checking access...',
            errorMessage: '',
            checkingAccess: true,
            loadingReportData: false,
            reportLoaded: false,
            currentReport: null,
            vessel: {},
            fleet: {},
            showImageModal: false,
            modalImageUrl: '',

            tab: '1',
            hideDone: false,
            doneTally: 0,
            reviewedItems: [],
            loadingReviewedItems: false,

            checklistLoaded: false,
            loadingChecklist: false,
            checklistCategories: [],
            checklistQuestions: [],
            checklistResponses: {},

            timelineEntries: [],
            timelineLoaded: false,
            loadingTimeline: false,
            statsAnimKey: 0,
            animateStats: true,

            contentTypes: {
                contacts: 'Important Contacts',
                policies: 'Policies',
                sops: 'Safe Work Procedures',
                eops: 'Emergency Procedures',
                hazards: 'Hazards & Risk Assessments',
                other: 'Other Documents',
                'asset-overview': 'Asset Overview',
                intro: 'SMS Introduction',
                'owner-info': 'Owner Information',
                logo: 'Fleet Logo',
                photo: 'Asset Photo',
                'asset-roles': 'Crew Roles',
                drill: 'Safety Drills',
                plant: 'Plant & Equipment',
                briefings: 'Safety Briefing Checklist',
                inductions: 'Crew Induction Checklist',
                maintenance: 'Maintenance Checklist',
                startups: 'Startup Checklist',
                shutdowns: 'Shutdown Checklist',
                statements: 'Responsibility Statements'
            }
        };
    },

    watch: {
        tab(val) {
            if (val === '6' && !this.timelineLoaded) this.loadTimeline();
        }
    },

    mounted() {
        const hasParam = this.$route.query && this.$route.query.x;
        if (hasParam) {
            this.xID = this.$route.query.x || '';
        }
        if (this.xID !== '') {
            this.checkCodeExists();
        } else {
            setTimeout(() => {
                this.checkingAccess = false;
                this.errorMessage = 'No report found.';
            }, 1000);
        }
    },

    methods: {
        simpleDate,

        questionsForCategory(categoryID) {
            return this.checklistQuestions.filter((q) => q.categoryID === categoryID);
        },

        truncate(string, limit) {
            return string && string.length > limit ? string.slice(0, limit) + '...' : string;
        },

        async checkCodeExists() {
            this.errorMessage = '';

            try {
                const result = await loadAssetAuditReport({ webLinkID: this.xID });
                const data = result.data;

                if (!data.success) {
                    this.checkingAccess = false;
                    this.status = 'This report could not be loaded.';
                    this.errorMessage = 'No report found.';
                    return;
                }

                // Populate store with weblink meta
                const weblinkMeta = data.weblinkMeta;
                this.$store.dispatch('setCurrentWeblinkMeta', { weblinkMeta });

                this.status = 'Loading report...';
                this.checkingAccess = false;
                this.loadingReportData = true;

                // Populate component state from cloud function response
                this.vessel = data.vessel ?? {};
                this.fleet = data.fleet ?? {};

                if (data.currentReport) {
                    this.currentReport = data.currentReport;
                    this.reportLoaded = true;
                } else {
                    this.errorMessage = 'Report data not found.';
                    this.loadingReportData = false;
                    return;
                }

                // Checklist data
                this.checklistCategories = data.checklistCategories ?? [];
                this.checklistQuestions = data.checklistQuestions ?? [];
                this.checklistResponses = data.checklistResponses ?? {};
                this.checklistLoaded = this.checklistCategories.length > 0;
                this.loadingChecklist = false;

                // Reviewed items
                this.reviewedItems = data.reviewedItems ?? [];
                this.doneTally = this.reviewedItems.filter((i) => i.done).length;
                this.loadingReviewedItems = false;

                // Timeline
                this.timelineEntries = data.timelineEntries ?? [];
                this.timelineLoaded = true;
                this.loadingTimeline = false;

                this.loadingReportData = false;
                this.status = '';
            } catch (error) {
                this.checkingAccess = false;
                this.status = 'This report could not be loaded.';
                this.errorMessage = 'No report found.';
            }
        },

        openImageModal(url) {
            this.modalImageUrl = url;
            this.showImageModal = true;
        }
    }
};
</script>

<style lang="scss" scoped>
.container {
    max-width: 1400px !important;
    padding: 1rem 20px !important;
}
.table {
    tbody {
        tr {
            th {
                width: 25%;
            }
        }
    }
}
.thumb-img {
    width: 48px;
    height: 48px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid #e4e4e4;
    flex-shrink: 0;
}
.follow-up {
    padding-bottom: 4px;
    .summary {
        white-space: pre-wrap;
        font-size: 0.9rem;
    }
}
.checklist-question-row {
    padding: 8px 0;
    border-bottom: 1px dashed #e4e4e4;
    &:last-of-type {
        border-bottom: none;
    }
    .question-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        .question-label {
            flex: 1;
            font-size: 0.9rem;
        }
    }
}
.chart-wrapper {
    max-width: 280px;
    margin: 0 auto;
    position: sticky;
    top: 90px;
}
.timeline-entry {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px dashed #e4e4e4;
    &:last-of-type {
        border-bottom: none;
    }
    .timeline-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: cadetblue;
        margin-top: 5px;
        flex-shrink: 0;
    }
    .timeline-body {
        .timeline-text {
            margin-bottom: 2px;
            font-size: 0.9rem;
        }
        .timeline-meta {
            font-size: 0.75rem;
            color: #aaa;
        }
    }
}
.vessel-stats-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 16px 28px;
    padding: 21px 0 0;
    background: #fff;
    border-top: 1px solid #e8e8e8;
    margin-top: 10px;
}
</style>
